/**
 * Load users/{uid} after Firebase Auth is ready.
 * Password reset revokes tokens; the first Firestore read can fail even though
 * the profile exists. Callers must not treat those failures as a new student.
 */

/** Backoff between Firestore profile retries after sign-in (ms). */
export const PROFILE_RETRY_DELAYS_MS = [250, 750, 1500];

/**
 * Firestore errors that are common when Auth tokens were just revoked
 * (password reset) and the persistent cache has not picked up the new token.
 */
export const isRetryableProfileError = (error) => {
  const code = String(error?.code || '').replace(/^firestore\//, '');
  return [
    'permission-denied',
    'unauthenticated',
    'unavailable',
    'deadline-exceeded',
    'failed-precondition',
    'resource-exhausted'
  ].includes(code);
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wait for an ID token, then read the user profile. Retries transient errors.
 * @param {import('firebase/auth').User} authUser
 * @param {(uid: string) => Promise<import('firebase/firestore').DocumentSnapshot>} getDocument
 * @param {number[]} [delays] Retry backoff delays in ms
 */
export const fetchUserProfileDocument = async (
  authUser,
  getDocument,
  delays = PROFILE_RETRY_DELAYS_MS
) => {
  await authUser.getIdToken();

  let lastError;
  const attempts = delays.length + 1;

  for (let i = 0; i < attempts; i += 1) {
    try {
      return await getDocument(authUser.uid);
    } catch (error) {
      lastError = error;
      if (i === attempts - 1 || !isRetryableProfileError(error)) {
        throw error;
      }
      await authUser.getIdToken(true);
      await delay(delays[i]);
    }
  }

  throw lastError;
};
